
import React, { useState } from 'react';
import { IconCube, IconPython, IconGit, IconCheckCircle, IconClose } from '../components/Icons';
import toast from 'react-hot-toast';

interface Model {
    id: string;
    name: string;
    type: 'Pricing' | 'Risk' | 'Curve';
    language: 'Python' | 'C++';
    version: string;
    status: 'Production' | 'UAT' | 'Dev' | 'Deprecated';
    lastUpdated: string;
    author: string;
    code: string;
}

const MOCK_MODELS: Model[] = [
    { 
        id: 'MDL-001', name: 'Black-Scholes (Vanilla)', type: 'Pricing', language: 'Python', version: 'v1.2.4', status: 'Production', lastUpdated: '2024-01-15', author: 'Quant_Team_A',
        code: `def black_scholes(S, K, T, r, sigma, type='call'):\n    d1 = (np.log(S/K) + (r + 0.5 * sigma**2) * T) / (sigma * np.sqrt(T))\n    d2 = d1 - sigma * np.sqrt(T)\n    if type == 'call':\n        price = S * norm.cdf(d1) - K * np.exp(-r*T) * norm.cdf(d2)\n    else:\n        price = K * np.exp(-r*T) * norm.cdf(-d2) - S * norm.cdf(-d1)\n    return price` 
    },
    { 
        id: 'MDL-002', name: 'Heston Stochastic Vol', type: 'Pricing', language: 'C++', version: 'v2.0.1', status: 'UAT', lastUpdated: '2024-05-10', author: 'Quant_Dev_Lead',
        code: `// Heston Model Implementation\n// Uses semi-analytical method via characteristic functions\ndouble heston_price(double S, double K, double T, double r, double v0, double theta, double kappa, double xi, double rho) {\n    // Integration logic here...\n    return price;\n}` 
    },
    { 
        id: 'MDL-003', name: 'Hull-White 1F (Rates)', type: 'Curve', language: 'Python', version: 'v0.9.5', status: 'Dev', lastUpdated: '2024-05-22', author: 'Intern_Bob',
        code: `class HullWhite1F:\n    def __init__(self, a, sigma):\n        self.a = a\n        self.sigma = sigma\n    \n    def short_rate_tree(self, steps, T):\n        # Trinomial tree construction\n        pass` 
    },
    { 
        id: 'MDL-004', name: 'Historical VaR Engine', type: 'Risk', language: 'Python', version: 'v3.1.0', status: 'Production', lastUpdated: '2023-11-05', author: 'Risk_IT',
        code: `def calculate_var(returns, confidence=0.99):\n    sorted_rets = np.sort(returns)\n    index = int((1-confidence) * len(sorted_rets))\n    return sorted_rets[index]` 
    }
];

type TypeFilter = 'All' | 'Pricing' | 'Risk' | 'Curve';
const TYPE_FILTERS: TypeFilter[] = ['All', 'Pricing', 'Risk', 'Curve'];

const ModelRegistryView: React.FC = () => {
    const [models, setModels] = useState<Model[]>(MOCK_MODELS);
    const [selectedModel, setSelectedModel] = useState<Model | null>(null);
    const [typeFilter, setTypeFilter] = useState<TypeFilter>('All');

    const getStatusColor = (status: Model['status']) => {
        switch(status) {
            case 'Production': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
            case 'UAT': return 'bg-blue-100 text-blue-700 border-blue-200';
            case 'Dev': return 'bg-amber-100 text-amber-700 border-amber-200';
            default: return 'bg-slate-100 text-slate-500 border-slate-200';
        }
    };

    const handleApprove = () => {
        if (!selectedModel) return;
        if (selectedModel.status === 'Production') return;
        
        const nextStatus = selectedModel.status === 'Dev' ? 'UAT' : 'Production';
        const updated = { ...selectedModel, status: nextStatus as Model['status'] };
        
        setModels(prev => prev.map(m => m.id === selectedModel.id ? updated : m));
        setSelectedModel(updated);
        toast.success(`Model promoted to ${nextStatus}`);
    };

    const filteredModels = typeFilter === 'All' ? models : models.filter(m => m.type === typeFilter);

    return (
        <div className="flex h-full bg-slate-50">
            {/* Left: List */}
            <div className="w-96 bg-white border-r border-slate-200 flex flex-col">
                <div className="p-6 border-b border-slate-100">
                    <div className="flex items-center justify-between mb-1">
                        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                            <IconCube className="text-violet-600" /> Model Registry
                        </h2>
                        <span className="text-xs font-mono text-slate-400 bg-slate-100 px-2 py-0.5 rounded">{filteredModels.length} models</span>
                    </div>
                    <p className="text-xs text-slate-500 mb-4">Governance & Version Control (SR 11-7)</p>
                    {/* Type filter tabs */}
                    <div className="flex gap-1">
                        {TYPE_FILTERS.map(f => (
                            <button
                                key={f}
                                onClick={() => setTypeFilter(f)}
                                className={`px-2.5 py-1 rounded text-xs font-bold transition-colors ${
                                    typeFilter === f
                                        ? 'bg-violet-600 text-white'
                                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                }`}
                            >{f}</button>
                        ))}
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto custom-scrollbar p-2">
                    {filteredModels.map(model => (
                        <div
                            key={model.id}
                            onClick={() => setSelectedModel(model)}
                            className={`p-4 mb-2 rounded-lg border cursor-pointer transition-all ${
                                selectedModel?.id === model.id
                                ? 'bg-violet-50 border-violet-400 shadow-sm'
                                : 'bg-white border-slate-200 hover:bg-slate-50'
                            }`}
                        >
                            <div className="flex justify-between items-start mb-2">
                                <span className="font-bold text-sm text-slate-800">{model.name}</span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold border ${getStatusColor(model.status)}`}>
                                    {model.status}
                                </span>
                            </div>
                            <div className="flex justify-between items-center text-xs text-slate-500">
                                <div className="flex items-center gap-2">
                                    <span className="flex items-center gap-1"><IconPython /> {model.language}</span>
                                    <span className="px-1.5 py-0.5 bg-slate-100 rounded text-slate-500">{model.type}</span>
                                </div>
                                <span className="font-mono">{model.version}</span>
                            </div>
                        </div>
                    ))}
                    {filteredModels.length === 0 && (
                        <div className="p-8 text-center text-slate-400 text-sm">No {typeFilter} models found.</div>
                    )}
                </div>
            </div>

            {/* Right: Detail & Code */}
            <div className="flex-1 flex flex-col overflow-hidden">
                {selectedModel ? (
                    <div className="flex-1 flex flex-col p-6 overflow-y-auto custom-scrollbar">
                        {/* Header Card */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 mb-6">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                                        {selectedModel.name}
                                        <span className="text-xs font-normal text-slate-400 bg-slate-100 px-2 py-1 rounded">{selectedModel.id}</span>
                                    </h1>
                                    <div className="flex items-center gap-4 text-sm text-slate-500 mt-2">
                                        <span className="flex items-center gap-1"><IconGit /> Branch: main</span>
                                        <span>Author: {selectedModel.author}</span>
                                        <span>Last Updated: {selectedModel.lastUpdated}</span>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    {selectedModel.status !== 'Production' && (
                                        <button
                                            onClick={handleApprove}
                                            className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-bold text-sm flex items-center gap-2 transition-colors"
                                        >
                                            <IconCheckCircle /> Promote to {selectedModel.status === 'Dev' ? 'UAT' : 'Prod'}
                                        </button>
                                    )}
                                    <button className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-lg font-bold text-sm transition-colors">
                                        Run Tests
                                    </button>
                                </div>
                            </div>
                            {/* Promotion lifecycle */}
                            <div className="flex items-center gap-2 pt-4 border-t border-slate-100">
                                {(['Dev', 'UAT', 'Production'] as const).map((stage, i) => {
                                    const stageOrder = { Dev: 0, UAT: 1, Production: 2 };
                                    const currentOrder = stageOrder[selectedModel.status as keyof typeof stageOrder] ?? -1;
                                    const isDone = stageOrder[stage] < currentOrder;
                                    const isCurrent = stage === selectedModel.status;
                                    return (
                                        <React.Fragment key={stage}>
                                            <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${
                                                isCurrent ? 'bg-violet-600 border-violet-600 text-white' :
                                                isDone    ? 'bg-emerald-50 border-emerald-300 text-emerald-600' :
                                                            'bg-slate-50 border-slate-200 text-slate-400'
                                            }`}>
                                                {isDone && <IconCheckCircle />}
                                                {stage}
                                            </div>
                                            {i < 2 && <div className={`flex-1 h-px ${isDone ? 'bg-emerald-300' : 'bg-slate-200'}`}/>}
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Code Viewer */}
                        <div className="flex-1 bg-slate-900 rounded-xl shadow-lg overflow-hidden flex flex-col border border-slate-700">
                            <div className="bg-slate-800 px-4 py-2 flex justify-between items-center border-b border-slate-700">
                                <span className="text-xs font-mono text-slate-300 flex items-center gap-2">
                                    <IconPython /> /models/pricing/{selectedModel.id.toLowerCase()}.py
                                </span>
                                <span className="text-[10px] text-slate-500">Read-Only</span>
                            </div>
                            <div className="flex-1 p-4 overflow-auto custom-scrollbar">
                                <pre className="font-mono text-sm text-blue-300">
                                    <code>{selectedModel.code}</code>
                                </pre>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
                        <div className="p-8 text-center max-w-sm">
                            <div className="w-16 h-16 rounded-2xl bg-violet-50 border border-violet-100 flex items-center justify-center mx-auto mb-4">
                                <IconCube className="text-violet-400" />
                            </div>
                            <p className="text-base font-semibold text-slate-600 mb-1">Select a model</p>
                            <p className="text-sm text-slate-400">Choose a model from the registry to inspect its code, version history, and promote it through the governance pipeline.</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ModelRegistryView;
